import { matchPath } from 'react-router-dom';
import { PATHS } from '../paths';
import { useModules } from '../../utils/modules';

/**
 * Central route -> permission table for the SPA. Port of the legacy
 * cc-web-controllers/Common/Security/UrlAccessManager.cs, keyed by route
 * pattern instead of controller/action.
 *
 * This is a UX gate only (it decides whether a page renders); the gateway /
 * services remain the real enforcement point for the data behind each page.
 *
 * Rules are evaluated against the raw snake_case `userView.permissions`
 * object (see proto-cloud/proto/user/v1/message.proto `Permissions`).
 *
 * A rule is one of:
 *   - ACCESS.PUBLIC         -> rendered without waiting for the session
 *   - ACCESS.AUTHENTICATED  -> any logged-in user
 *   - (permissions, ctx) => boolean
 *
 * Paths not listed fall through to "any authenticated user", same as the
 * trailing `return true` of the C# manager.
 */
export const ACCESS = Object.freeze({
    PUBLIC: 'public',
    AUTHENTICATED: 'authenticated',
});

const anyOf = (...keys) => (p) => keys.some((k) => !!p?.[k]);

const parseJson = (value) => {
    if (!value) return {};
    if (typeof value === 'object') return value;
    try {
        return JSON.parse(value) || {};
    } catch (e) {
        console.error('urlAccessManager: failed to parse permission JSON:', e);
        return {};
    }
};

export const isMercuryAdmin = (p) => {
    if (!p?.can_access_mercury) return false;
    const permissions = parseJson(p.mercury_permissions).permissions || [];
    const admin = permissions.find((item) => item?.key === 'is_admin');
    return !!admin?.is_checked;
};

export const hasLetterAdmin = (p) => {
    const manageLetters = parseJson(p?.letter_admin_permissions).manage_letters || [];
    return manageLetters.some((item) => !!item?.is_checked);
};

const canAccessWorkkards = anyOf(
    'can_create_workkard',
    'can_archive_workkard',
    'can_change_workkard_sponsor',
    'can_receive_un_assigned_workkards',
    'can_close_workkard',
    'can_edit_workkard',
    'can_reopen_workkard',
);

const canAccessActivities = anyOf(
    'can_create_activity',
    'can_edit_activity',
    'can_reassign_activity',
    'can_accept_reject_activity',
    'can_close_activity',
    'can_archive_activity',
    'can_accept_reject_bid',
    'can_receive_un_assigned_activities',
    'can_reopen_activity',
    'can_change_activity',
    'can_close_activity_during_mid_term',
    'can_mark_activity_as_in_progress',
    'can_assign_b2b',
);

const canAccessRelationships = anyOf(
    'can_send_b2b_relationship_request',
    'can_accept_reject_b2b_relationship_request',
    'can_manage_contracts',
    'can_de_associate_b2b_relationship',
    'can_add_remove_organization_from_exclude_list',
    'can_view_b2b_workload',
);

const canAccessUsers = anyOf(
    'can_add_user',
    'can_re_assign_a_user',
    'can_edit_user',
    'can_block_a_user',
    'can_reset_password',
    'can_view_workload',
);

/**
 * Ordered: the first matching pattern wins, so specific paths
 * (e.g. /users/add) must precede general ones (/users).
 */
export const ROUTE_ACCESS_RULES = Object.freeze([
    // ---------------------------- Public ----------------------------
    { path: `/${PATHS.AGREEMENT}/*`, rule: ACCESS.PUBLIC },
    { path: `/${PATHS.Privacy_Policy}/*`, rule: ACCESS.PUBLIC },

    // ----------------------- Authenticated only ----------------------
    { path: PATHS.WORKKARD_DETAIL, rule: ACCESS.AUTHENTICATED },
    { path: PATHS.ACTIVITY_DETAIL, rule: ACCESS.AUTHENTICATED },
    { path: PATHS.PRINT_WORKKARD_DETAIL, rule: ACCESS.AUTHENTICATED },
    { path: `${PATHS.VIEWS}/*`, rule: ACCESS.AUTHENTICATED },
    { path: PATHS.DIARIES, rule: ACCESS.AUTHENTICATED },
    { path: PATHS.TDO, rule: ACCESS.AUTHENTICATED },
    { path: PATHS.USER_PROFILE, rule: ACCESS.AUTHENTICATED },

    // ---------------------------- Workkard ---------------------------
    { path: PATHS.WORKKARD_CREATE, rule: anyOf('can_create_workkard') },
    { path: PATHS.WORKKARD_EDIT, rule: anyOf('can_edit_workkard', 'can_edit_closed_workkard') },
    { path: PATHS.WORKKARDS, rule: canAccessWorkkards },
    { path: PATHS.WORKKARD_LIST_LEGACY, rule: canAccessWorkkards },
    { path: PATHS.POLICIES, rule: canAccessWorkkards },

    // ---------------------------- Activity ---------------------------
    { path: PATHS.ACTIVITY_CREATE, rule: anyOf('can_create_activity') },
    { path: PATHS.ACTIVITY_EDIT, rule: anyOf('can_edit_activity') },
    { path: PATHS.ACTIVITIES, rule: canAccessActivities },
    { path: PATHS.REQUESTS, rule: canAccessActivities },

    // ----------------------------- Admin -----------------------------
    { path: PATHS.ORG_PROFILE, rule: anyOf('can_manage_company_profile') },
    { path: PATHS.RELATIONSHIPS, rule: canAccessRelationships },
    { path: PATHS.USER_ADD, rule: anyOf('can_add_user') },
    { path: PATHS.USER_EDIT, rule: anyOf('can_edit_user') },
    { path: PATHS.MANAGE_WORKLOAD, rule: anyOf('can_view_workload') },
    { path: PATHS.USERS, rule: canAccessUsers },
    { path: PATHS.ORG_ACCESS, rule: anyOf('invite_people_to_access_community') },
    {
        path: PATHS.RULE_ENGINE,
        rule: (p) => process.env.enable_rule_engine == 'true' && !!p?.can_manage_rules,
    },
    {
        path: PATHS.PARTIES,
        rule: (p, { isModuleEnabled }) => !!process.env.enable_associated_party
            && !!(p?.can_create_party || p?.can_delete_party)
            && isModuleEnabled('parties'),
    },
    {
        path: PATHS.INBOX_ADMIN,
        rule: (p, { isModuleEnabled }) => !!p?.can_manage_inbox && isModuleEnabled('inbox'),
    },
    { path: PATHS.MERCURY_ADMIN, rule: (p) => isMercuryAdmin(p) },
    {
        path: PATHS.MANAGE_COMM,
        rule: (p, { isModuleEnabled }) => !!p?.can_manage_communication && isModuleEnabled('admin.communication'),
    },
    {
        path: PATHS.LETTER_ADMIN,
        rule: (p, { isModuleEnabled }) => hasLetterAdmin(p) && isModuleEnabled('letters'),
    },
    { path: PATHS.SETTINGS, rule: anyOf('can_manage_settings') },
]);

/**
 * @param {string} pathname - location pathname, e.g. "/users/edit/12"
 * @returns {string|Function} the matching rule, or ACCESS.AUTHENTICATED when unlisted.
 */
export const getRouteRule = (pathname) => {
    const entry = ROUTE_ACCESS_RULES.find(({ path }) =>
        matchPath({ path, caseSensitive: false, end: true }, pathname || '/'));
    return entry ? entry.rule : ACCESS.AUTHENTICATED;
};

export const isPublicRoute = (pathname) => getRouteRule(pathname) === ACCESS.PUBLIC;

/**
 * Equivalent of UrlAccessManager.IsActionAllowedForUser.
 *
 * @param {string} pathname
 * @param {object|null} permissions - `userView.permissions`; null means not authenticated.
 * @param {{ isModuleEnabled?: (key: string) => boolean }} [ctx]
 * @returns {boolean}
 */
export const isRouteAllowedForUser = (pathname, permissions, ctx = {}) => {
    const rule = getRouteRule(pathname);
    if (rule === ACCESS.PUBLIC) return true;
    if (!permissions) return false;
    if (rule === ACCESS.AUTHENTICATED) return true;

    const isModuleEnabled = ctx.isModuleEnabled || useModules().isModuleEnabled;
    return !!rule(permissions, { isModuleEnabled });
};
